import { useEffect, useState } from "react";
import { ArrowUpRight, CalendarDays, ChevronDown, Clock, MapPin } from "lucide-react";
import { scheduledShows, tour } from "../lib/tour";

interface PastShow {
  id: number | string;
  title: string;
  venue: string;
  date: string;
  time?: string;
  location?: string;
  flyer?: string;
}

const pastShows: PastShow[] = [
  {
    id: 1,
    title: "Rock 'N Roll on the Rooftop",
    venue: "Lakewood Ranch Library Rooftop",
    date: "January 10, 2026",
    time: "5:00 PM - 7:00 PM",
    location: "Lakewood Ranch, FL",
  },
  {
    id: 2,
    title: "End of Season Rock Festival",
    venue: "Joyland",
    date: "January 17-18, 2026",
    location: "Sarasota, FL",
  },
  {
    id: 3,
    title: "Thunder by the Bay",
    venue: "Thunder by the Bay",
    date: "February 14, 2026",
    time: "3:00 PM - 4:30 PM",
    location: "Sarasota, FL",
  },
  {
    id: 4,
    title: "BAM!Fest 2026",
    venue: "Bradenton Riverwalk, 452 3rd Ave W",
    date: "March 28, 2026",
    time: "1:00 PM",
    location: "Bradenton, FL 34205",
  },
  {
    id: 15,
    title: "Fort Myers Houseband Collab",
    venue: "17200 S Tamiami Trail",
    date: "April 26, 2026",
    time: "5:00 PM - 8:00 PM",
    location: "Fort Myers, FL 33908",
  },
  {
    id: 16,
    title: "Rock N Barrel Live",
    venue: "Rock N Barrel Bar and Grill",
    date: "May 9, 2026",
    time: "1:00 PM - 5:00 PM",
    location: "6916 14th Street West, Bradenton, FL 34207",
  },
  {
    id: 5,
    title: "Season Final Performances",
    venue: "Joyland",
    date: "May 16, 2026",
    time: "10:00 AM - 9:00 PM",
    location: "Sarasota, FL",
  },
  {
    id: 6,
    title: "Spring Performance",
    venue: "Joyland",
    date: "May 17, 2026",
    time: "1:00 PM - 5:00 PM",
    location: "Bradenton, FL",
  },
  {
    id: 7,
    title: "New York, NY",
    venue: "Train Car Park",
    date: "June 23, 2026",
    time: "4:00 PM - 9:00 PM",
    location: "Rose Ave, Port Jefferson Station, NY 11776",
  },
  {
    id: 8,
    title: "Seaside Heights, NJ",
    venue: "Hemingway's Cafe",
    date: "June 24, 2026",
    time: "6:00 PM - 11:00 PM",
    location: "612 Boulevard, Seaside Heights, NJ 08751",
  },
  {
    id: 9,
    title: "Ft. Washington, PA",
    venue: "SOR Ft. Washington",
    date: "June 25, 2026",
    time: "4:00 PM - 9:30 PM",
    location: "425A Delaware Dr, Fort Washington, PA 19034",
  },
  {
    id: 10,
    title: "Richmond, VA",
    venue: "Uptown Alley",
    date: "June 26, 2026",
    time: "12:00 PM - 6:00 PM",
    location: "6101 Brad McNeer Pkwy, Midlothian, VA 23112",
  },
  {
    id: 11,
    title: "Pittsboro, NC",
    venue: "The Mod",
    date: "June 27, 2026",
    time: "1:00 PM - 6:00 PM",
    location: "46 Sanford Rd, Pittsboro, NC 27312",
  },
  {
    id: 12,
    title: "Avondale Estates, GA",
    venue: "Music Avon Theater",
    date: "June 28, 2026",
    time: "1:00 PM - 6:00 PM",
    location: "106 N Avondale Rd, Avondale Estates, GA 30002",
  },
  {
    id: 13,
    title: "Coral Springs, FL",
    venue: "Sharkey's Bar & Grill",
    date: "June 30, 2026",
    time: "7:00 PM - 11:30 PM",
    location: "10365 Royal Palm Blvd, Coral Springs, FL 33065",
  },
  {
    id: 17,
    title: "Summer Show",
    venue: "Joyland",
    date: "August 16, 2026",
    time: "12:00 PM - 6:00 PM",
    location: "8333 Lockwood Ridge Rd, Sarasota, FL",
  },
];

function formatEventDate(value: string, options: Intl.DateTimeFormatOptions) {
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", ...options }).format(new Date(value));
}

function festivalHours(show: (typeof scheduledShows)[number]) {
  const options: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };
  return `${formatEventDate(show.startsAt, options)} - ${formatEventDate(show.endsAt, options)}`;
}

export function Shows() {
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const upcoming = scheduledShows.filter((show) => Date.parse(show.endsAt) > now);
  const archivedShows: PastShow[] = [
    ...pastShows,
    ...scheduledShows.filter((show) => Date.parse(show.endsAt) <= now).map((show) => ({
      id: show.id,
      title: show.title,
      venue: show.venue,
      date: formatEventDate(show.startsAt, { year: "numeric", month: "long", day: "numeric" }),
      time: `Hours: ${festivalHours(show)}`,
      flyer: "flyer" in show ? show.flyer : undefined,
    })),
  ];

  return (
    <section id="shows" className="tour-section" aria-labelledby="shows-title">
      <div className="shell">
        <div className="section-heading">
          <div>
            <p className="eyebrow"><span>02</span> / Shows &amp; summer tour</p>
            <h2 id="shows-title">Catch us<br /><em>live.</em></h2>
          </div>
          <p className="section-intro">From the next local show to the {tour.year} summer tour. Here's where to find us.</p>
        </div>

        {upcoming.map((show, index) => (
          <article className="upcoming-show" key={show.id} aria-labelledby={`${show.id}-title`}>
            <time className="upcoming-date" dateTime={show.startsAt}>
              <span>{formatEventDate(show.startsAt, { month: "short" })}</span>
              <strong>{formatEventDate(show.startsAt, { day: "numeric" })}</strong>
              <span>{formatEventDate(show.startsAt, { year: "numeric" })}</span>
            </time>
            <div className="upcoming-show-copy">
              <p className="eyebrow">{index === 0 ? "Next show" : "Upcoming"}</p>
              <h3 id={`${show.id}-title`}>{show.title}</h3>
              <p className="show-weekday">{formatEventDate(show.startsAt, { dateStyle: "full" })}</p>
              <p className="show-detail"><Clock aria-hidden="true" /><span>Hours: {festivalHours(show)}</span></p>
              <p className="show-detail"><MapPin aria-hidden="true" /><span>{show.venue}</span></p>
              {"description" in show && show.description ? (
                <p className="show-description">{show.description}</p>
              ) : null}
            </div>
            {"flyer" in show && show.flyer ? (
              <a className="show-flyer" href={show.flyer} target="_blank" rel="noopener noreferrer">
                <img
                  src={show.flyer}
                  srcSet={
                    show.id === "bridgewater-fall-festival-2026"
                      ? `/images/bridgewater-fall-festival-2026-480.webp 480w, /images/bridgewater-fall-festival-2026-960.webp 960w, ${show.flyer} 1290w`
                      : undefined
                  }
                  sizes="(max-width: 700px) 100vw, 320px"
                  alt={`${show.title} flyer`}
                  width={1290}
                  height={1669}
                  loading="lazy"
                />
                <span>View event flyer <ArrowUpRight aria-hidden="true" /></span>
              </a>
            ) : null}
          </article>
        ))}

        <div className="tour-announcement">
          <div className="tour-date-block"><CalendarDays aria-hidden="true" /><span>Summer</span><strong>{tour.year}</strong></div>
          <div className="tour-announcement-copy">
            <span className="announcement-label">Dates to be announced</span>
            <h3>Summer {tour.year}: the road ahead.</h3>
            <p>Confirmed cities, venues, and show dates will be posted here. Until then, follow the band for announcements.</p>
          </div>
          <a className="button button-secondary" href={tour.instagramUrl} target="_blank" rel="noopener noreferrer">
            Follow for updates <ArrowUpRight aria-hidden="true" />
          </a>
        </div>

        <details className="show-archive">
          <summary><span>Rewind: past shows</span><span className="archive-summary-count">{archivedShows.length} past shows</span><ChevronDown aria-hidden="true" /></summary>
          <p className="archive-description">A look back at previous performances. These are historical dates, not upcoming shows.</p>
          <div className="past-show-list">
            {[...archivedShows].reverse().map((show) => (
              <a
                className="past-show"
                key={show.id}
                href={show.flyer ?? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${show.venue}, ${show.location}`)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="past-show-date">{show.date}<small>{show.time}</small></span>
                <span className="past-show-details"><strong>{show.title}</strong><span>{show.venue}</span></span>
                <span className="past-show-location">{show.location && <><MapPin aria-hidden="true" />{show.location}</>}</span>
                <ArrowUpRight aria-hidden="true" />
              </a>
            ))}
          </div>
        </details>
      </div>
    </section>
  );
}
